import history from 'src/history'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import { selectHomebotListingsFeature } from 'src/store/selectors/customerProfile'
import {
  selectClickedListItem,
  selectListItemLoading,
  setClickedListItem,
  setListItemLoading,
  setMobileNavIsOpen,
} from 'src/store/slices/navbar'
import { selectActiveItem } from 'src/store/selectors/navigation'
import {
  selectRouteToSearch,
  selectRouteToSavedSearches,
  selectRouteToFavoriteListings,
} from 'src/store/selectors/routes'
import {
  FIND_A_HOME_DROPDOWN_TAB_NAMES,
  NAVIGATION_DELAY,
} from 'src/constants/navigation'
import { DropdownItem } from 'src/Components/shared/Navbar/Dropdown/DropdownItem'
import { SvgWrapper } from 'src/Components/shared/Navbar/SvgWrapper'
import { Divider } from 'src/Components/shared/Navbar/Divider'
import { Spinner } from 'src/Components/shared/Navbar/Spinner'
import { ListItem } from 'src/Components/shared/Navbar/ListItem'
import { Text } from 'src/Components'
import { BellSvg, HeartSvg, MagnifyingGlassSvg } from 'src/svgs'

export interface SharedHomeSearchLinksProps {
  isMobile?: boolean
}

export const SharedHomeSearchLinks = ({
  isMobile,
}: SharedHomeSearchLinksProps) => {
  const dispatch = useDispatch()
  const searchEnabled = useSelector(selectHomebotListingsFeature)

  const loading = useSelector(selectListItemLoading)
  const activeDropdownItem = useSelector(selectActiveItem)
  const clickedListItem = useSelector(selectClickedListItem)

  const { FAVORITE_LISTINGS, SAVED_SEARCHES, SEARCH_HOMES, LISTING } =
    FIND_A_HOME_DROPDOWN_TAB_NAMES

  // Routes
  const searchTo = useSelector(selectRouteToSearch)
  const favoriteListingsTo = useSelector(selectRouteToFavoriteListings)
  const savedSearchesTo = useSelector(selectRouteToSavedSearches)

  const mappedKeyToRoute = {
    [SEARCH_HOMES]: searchTo,
    [FAVORITE_LISTINGS]: favoriteListingsTo,
    [SAVED_SEARCHES]: savedSearchesTo,
  }

  const handleOnClick = key => {
    dispatch(setClickedListItem(key))
    dispatch(setListItemLoading(true))

    setTimeout(() => {
      isMobile && dispatch(setMobileNavIsOpen(false))
      dispatch(setListItemLoading(false))
      history.push(mappedKeyToRoute[key])
    }, NAVIGATION_DELAY)
  }

  const renderSvgOrLoading = (key, svg) => {
    if (loading && clickedListItem === key) {
      return <Spinner />
    }

    return <SvgWrapper>{svg}</SvgWrapper>
  }

  if (!searchEnabled) return null

  const searchHomesIsActive =
    (activeDropdownItem === SEARCH_HOMES || activeDropdownItem === LISTING) &&
    !loading

  const Element = isMobile ? ListItem : DropdownItem

  return (
    <>
      <Element
        key={SEARCH_HOMES}
        isActive={searchHomesIsActive}
        onClick={() => handleOnClick(SEARCH_HOMES)}
      >
        {renderSvgOrLoading(SEARCH_HOMES, <MagnifyingGlassSvg />)}
        <Text
          weight='semibold'
          element='span'
          variant='neutralWarm'
          variantCount='700'
        >
          <FormattedMessage
            id='HomeSearchLinks.searchHome.button'
            defaultMessage='Search homes'
          />
        </Text>
      </Element>
      <Element
        key={FAVORITE_LISTINGS}
        isActive={activeDropdownItem === FAVORITE_LISTINGS && !loading}
        onClick={() => handleOnClick(FAVORITE_LISTINGS)}
      >
        {renderSvgOrLoading(FAVORITE_LISTINGS, <HeartSvg />)}
        <Text
          weight='semibold'
          element='span'
          variant='neutralWarm'
          variantCount='700'
        >
          <FormattedMessage
            id='HomeSearchLinks.favorite.button'
            defaultMessage='Favorite listings'
          />
        </Text>
      </Element>
      <Element
        key={SAVED_SEARCHES}
        isActive={activeDropdownItem === SAVED_SEARCHES && !loading}
        onClick={() => handleOnClick(SAVED_SEARCHES)}
      >
        {renderSvgOrLoading(SAVED_SEARCHES, <BellSvg />)}
        <Text
          weight='semibold'
          element='span'
          variant='neutralWarm'
          variantCount='700'
        >
          <FormattedMessage
            id='HomeSearchLinks.saved.button'
            defaultMessage='Saved searches'
          />
        </Text>
      </Element>
      <Divider />
    </>
  )
}
